import type { CarouselEffect } from "./carousel-settings";

export interface CarouselKeyframes {
  incoming: Keyframe[];
  outgoing: Keyframe[];
}
/** One change: the slides themselves and, for some effects, the picture (`data-carousel-media`) and the text
 *  (`data-carousel-copy`) inside them. */
export interface CarouselFrames {
  slide: CarouselKeyframes;
  media?: CarouselKeyframes;
  copy?: CarouselKeyframes;
}

const ZOOM_SCALE = 1.15;
const BARE_ZOOM_SCALE = 1.04;
const KEN_BURNS_SCALE = 1.12;
/** Ken Burns keeps moving for this long when nothing changes by itself, so a hidden Visningstid has no effect. */
const KEN_BURNS_STILL_INTERVAL = 8000;
/** The old »Fokus bolig« slider's parallax values in px: the picture and, from 1024 px, the text further ahead. */
const PARALLAX = { media: 100, copy: 800, narrowCopy: 100, wideFrom: 1024 };

const fade = (): CarouselKeyframes => ({
  incoming: [{ opacity: 0 }, { opacity: 1 }],
  outgoing: [
    { opacity: 1, visibility: "visible" },
    { opacity: 0, visibility: "visible" },
  ],
});
const slide = (d: 1 | -1): CarouselKeyframes => ({
  incoming: [{ transform: `translateX(${d * 100}%)` }, { transform: "none" }],
  outgoing: [
    { transform: "none", visibility: "visible" },
    { transform: `translateX(${-d * 100}%)`, visibility: "visible" },
  ],
});
const ahead = (d: 1 | -1, px: number): CarouselKeyframes => ({
  incoming: [{ transform: `translateX(${d * px}px)` }, { transform: "none" }],
  outgoing: [{ transform: "none" }, { transform: `translateX(${-d * px}px)` }],
});

/** `d` is 1 for Næste, a swipe to the left and every automatic change, -1 for Forrige and a swipe to the right;
 *  `media`/`copy` say whether the slides mark a picture or a text; `origin` is the picture's focus point. */
export function carouselKeyframes(
  effect: CarouselEffect,
  d: 1 | -1,
  {
    width,
    media = false,
    copy = false,
    origin = "center",
  }: { width: number; media?: boolean; copy?: boolean; origin?: string },
): CarouselFrames {
  switch (effect) {
    case "none":
      return { slide: { incoming: [], outgoing: [] } };
    case "slide":
      return { slide: slide(d) };
    case "rise":
      return {
        slide: {
          incoming: [
            { opacity: 0, transform: "translateY(24px)" },
            { opacity: 1, transform: "none" },
          ],
          outgoing: [
            { opacity: 1, transform: "none", visibility: "visible" },
            {
              opacity: 0,
              transform: "translateY(-12px)",
              visibility: "visible",
            },
          ],
        },
      };
    case "zoom":
      return media
        ? {
            slide: fade(),
            media: {
              incoming: [
                { transform: `scale(${ZOOM_SCALE})`, transformOrigin: origin },
                { transform: "scale(1)", transformOrigin: origin },
              ],
              outgoing: [],
            },
          }
        : {
            slide: {
              incoming: [
                { opacity: 0, transform: `scale(${BARE_ZOOM_SCALE})` },
                { opacity: 1, transform: "scale(1)" },
              ],
              outgoing: fade().outgoing,
            },
          };
    case "parallax":
      return {
        slide: slide(d),
        ...(media ? { media: ahead(d, PARALLAX.media) } : {}),
        ...(copy
          ? {
              copy: ahead(
                d,
                width >= PARALLAX.wideFrom
                  ? PARALLAX.copy
                  : PARALLAX.narrowCopy,
              ),
            }
          : {}),
      };
    case "kenBurns":
    case "fade":
    default:
      return { slide: fade() };
  }
}

/** Ken Burns' slow move of a slide's picture: in on even slides, out on odd ones, so consecutive slides alternate. */
export function kenBurnsKeyframes(
  index: number,
  origin = "center",
): Keyframe[] {
  const frames = [
    { transform: "scale(1)", transformOrigin: origin },
    { transform: `scale(${KEN_BURNS_SCALE})`, transformOrigin: origin },
  ];
  return index % 2 ? frames.reverse() : frames;
}

/** Ken Burns lasts the whole time a picture is seen: its fade in, its standing time and its fade out. */
export function kenBurnsDuration(
  effectDuration: number,
  interval: number,
  autoplay: boolean,
): number {
  return (
    effectDuration +
    (autoplay ? interval : KEN_BURNS_STILL_INTERVAL) +
    effectDuration
  );
}
